import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import DataBlock from './DataBlock.js';
import { getPercentageIncrease } from '../../common/globals.js';

function DataGroup({
  title, caseTypesSalesData,
  stickyStart = 0, displaySales = true, separateBy
}) {
  const groupHead = useRef();
  const [groupHeadHeight, setGroupHeadHeight] = useState(0);
  const totalData = useMemo(() => getTotalData(caseTypesSalesData, separateBy),
    [caseTypesSalesData, separateBy]);

  useEffect(() => {
    if (groupHead.current) setGroupHeadHeight(groupHead.current.clientHeight);
  }, [groupHead.current]);

  return (
    <>
      <h4
        ref={groupHead}
        className="optimize-section-header"
        style={{
          position: 'sticky',
          top: `${stickyStart}px`,
        }}
      >
        {title}
      </h4>
      { caseTypesSalesData ? (
        <DataBlock
          title="Total"
          data={totalData}
          stickyStart={groupHeadHeight + stickyStart}
          displaySales={displaySales}
        />
      ) : null }
      { caseTypesSalesData ? (
        Object.keys(caseTypesSalesData).sort().map((caseType) => {
          const dataForType = getDataForCaseType(caseTypesSalesData[caseType], separateBy);

          return (
            <DataBlock
              key={caseType}
              title={caseType}
              data={dataForType}
              stickyStart={groupHeadHeight + stickyStart}
              displaySales={displaySales}
            />
          );
        })) : null }
    </>
  );
}

const getDataForCaseType = (salesData, separationType) => {
  if (separationType) {
    const uniqueTypes = [...new Set(salesData.map((item) => item[separationType]))];
    return uniqueTypes.map((type) => {
      const filteredData = salesData
        .filter((item) => item[separationType] === type);
      return getTotalFromArray(filteredData, type);
    });
  }
  return [getTotalFromArray(salesData)];
};

const getTotalData = (salesData, separateBy) => {
  if (!salesData || salesData.length === 0) return [];
  const allCases = [];
  Object.keys(salesData).forEach((caseType) => {
    allCases.push(...salesData[caseType]);
  });
  if (separateBy) {
    const uniqueTypes = [...new Set(allCases.map((item) => item[separateBy]))];
    return uniqueTypes.map((type) => {
      const filteredData = allCases.filter((item) => item[separateBy] === type);
      return getTotalFromArray(filteredData, type);
    });
  }
  return [getTotalFromArray(allCases)];
};

const getTotalFromArray = (data, typeName) => {
  if (!data || data.length === 0) return {};
  const reducedData = data.reduce((acc, item) => ({
    PY: acc.PY,
    CY: acc.CY,
    SalesPY: acc.SalesPY + item.SalesPY,
    SalesPYTD: acc.SalesPYTD + item.SalesPYTD,
    SalesCYTD: acc.SalesCYTD + item.SalesCYTD,
    CM: acc.CM,
    SalesPM: acc.SalesPM + item.SalesPM,
    SalesPMTD: acc.SalesPMTD + item.SalesPMTD,
    SalesCMTD: acc.SalesCMTD + item.SalesCMTD,
    NotBilled: acc.NotBilled + item.NotBilled,
    YTD_Chg: acc.YTD_Chg,
    MTD_Chg: acc.MTD_Chg
  }), {
    PY: data[0].PY,
    CY: data[0].CY,
    SalesPY: 0,
    SalesPYTD: 0,
    SalesCYTD: 0,
    CM: data[0].CM,
    SalesPM: 0,
    SalesPMTD: 0,
    SalesCMTD: 0,
    NotBilled: 0,
    YTD_Chg: null,
    MTD_Chg: null
  });
  reducedData.YTD_Chg = getPercentageIncrease(reducedData.SalesCYTD, reducedData.SalesPYTD);
  reducedData.MTD_Chg = getPercentageIncrease(reducedData.SalesCMTD, reducedData.SalesPMTD);

  if (typeName) {
    reducedData.type = typeName;
  }
  return reducedData;
};

export default DataGroup;
